import { useEffect, useRef, type CSSProperties } from 'react';
import { crawlerSetting } from '@/app/Crawl/config/modalSettingsConfig';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { Col, Form, Row, Tab } from 'react-bootstrap';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import UrlScopeDropdown, {
  buildStartCrawlSettings,
  includePatternsFromSettings,
  type UrlScope
} from '@/app/Crawl/components/common/UrlScopeDropdown';

interface MainSetting {
  name: string;
  text: string;
  defaultValue: boolean | string;
  options: {
    value: string;
    label: string;
  }[];
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

type SetFieldValue = (
  field: string,
  value: unknown,
  shouldValidate?: boolean
) => Promise<unknown>;

interface MainProps {
  values: Record<string, unknown>;
  setFieldValue: SetFieldValue;
  domain?: string | null;
  startUrl?: string;
  urlScope: UrlScope;
  onUrlScopeChange: (scope: UrlScope) => void;
}

const asBoolSwitchValue = (
  fieldValue: unknown,
  fallback: boolean
): 'true' | 'false' => {
  if (fieldValue === undefined || fieldValue === null) {
    return fallback ? 'true' : 'false';
  }
  if (
    fieldValue === true ||
    fieldValue === 'true' ||
    fieldValue === 1 ||
    fieldValue === '1'
  ) {
    return 'true';
  }
  if (
    fieldValue === false ||
    fieldValue === 'false' ||
    fieldValue === 0 ||
    fieldValue === '0'
  ) {
    return 'false';
  }
  return fallback ? 'true' : 'false';
};

const applyScopeSettings = (
  scope: UrlScope,
  startUrl: string,
  setFieldValue: SetFieldValue
) => {
  const settings = buildStartCrawlSettings(scope, startUrl);
  void setFieldValue('url_scope', scope);
  void setFieldValue('domain_scope', settings.domain_scope);
  void setFieldValue('include_urls_is_enable', settings.include_urls_is_enable);
  void setFieldValue(
    'include_urls_value',
    includePatternsFromSettings(settings)
  );
};

const Main = ({
  values,
  setFieldValue,
  domain,
  startUrl = '',
  urlScope,
  onUrlScopeChange
}: MainProps) => {
  const settings = crawlerSetting as MainSetting[];
  const switchSettings = settings.filter(
    item => typeof item.defaultValue === 'boolean'
  );
  const skipScopeApply = useRef(true);
  const startUrlRef = useRef(startUrl);
  startUrlRef.current = startUrl;

  useEffect(() => {
    void setFieldValue('url_scope', urlScope);
    if (urlScope === 'url' || urlScope === 'path') {
      const next = buildStartCrawlSettings(urlScope, startUrl);
      void setFieldValue('include_urls_is_enable', true);
      void setFieldValue(
        'include_urls_value',
        includePatternsFromSettings(next)
      );
      void setFieldValue('domain_scope', 'host');
      return;
    }
    void setFieldValue('domain_scope', urlScope === 'root' ? 'root' : 'host');
  }, [startUrl, urlScope, setFieldValue]);

  useEffect(() => {
    if (skipScopeApply.current) {
      skipScopeApply.current = false;
      return;
    }
    applyScopeSettings(urlScope, startUrlRef.current, setFieldValue);
  }, [urlScope, setFieldValue]);

  const handleScopeChange = (scope: UrlScope) => {
    skipScopeApply.current = true;
    applyScopeSettings(scope, startUrl, setFieldValue);
    onUrlScopeChange(scope);
  };

  const renderSetting = (item: MainSetting, boxed = false) => {
    const fieldValue = values[item.name];
    const value =
      typeof item.defaultValue === 'boolean'
        ? asBoolSwitchValue(fieldValue, item.defaultValue)
        : typeof fieldValue === 'string'
          ? fieldValue
          : item.defaultValue;
    const label = (
      <span className="d-inline-flex align-items-center">
        {item.text}
        <HelpTooltip
          html={crawlTooltip.crawlSettings.crawlerSettings[item.name]}
        />
      </span>
    );

    return (
      <Col
        key={item.name}
        xs={12}
        md={6}
      >
        <div
          className={
            boxed
              ? 'crawl-settings-box border rounded-2 p-3 h-100'
              : undefined
          }
        >
          {typeof item.defaultValue === 'boolean' ? (
            <div className="d-flex align-items-center justify-content-between gap-3 py-1">
              <Form.Label htmlFor={item.name} className="mb-0">
                {label}
              </Form.Label>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <span className="fs--1 text-700">
                  {value === 'true' ? 'بله' : 'خیر'}
                </span>
                <Form.Check
                  type="switch"
                  id={item.name}
                  name={item.name}
                  checked={value === 'true'}
                  disabled={item.disabled}
                  onChange={event =>
                    void setFieldValue(item.name, event.target.checked)
                  }
                  aria-label={item.text}
                />
              </div>
            </div>
          ) : (
            <>
              <Form.Label htmlFor={item.name} className="mb-1">
                {label}
              </Form.Label>
              <Form.Select
                id={item.name}
                name={item.name}
                value={value}
                disabled={item.disabled}
                onChange={event =>
                  void setFieldValue(item.name, event.target.value)
                }
              >
                {item.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </>
          )}
          {item.helperText ? (
            <Form.Text className="d-block mt-1 fs--2 text-700">
              {item.helperText}
            </Form.Text>
          ) : null}
        </div>
      </Col>
    );
  };

  return (
    <Tab.Pane className="my-3" eventKey="crawlerSetting">
      <div dir="rtl" className="text-start">
        <Row className="g-3 mb-3">
          <Col xs={12} className="col-auto">
            <Form.Label htmlFor="crawl-url-scope-settings" className="mb-1">
              <span className="d-inline-flex align-items-center">
                محدوده دامنه
                <HelpTooltip
                  html={crawlTooltip.crawlSettings.crawlerSettings.domain_scope}
                  placement="left"
                  overlayStyle={
                    {
                      '--falcon-tooltip-max-width': '24rem'
                    } as CSSProperties
                  }
                />
              </span>
            </Form.Label>
            <UrlScopeDropdown
              domain={domain || ''}
              value={urlScope}
              onChange={handleScopeChange}
              variant="form"
              compact
              id="crawl-url-scope-settings"
            />
          </Col>
        </Row>
        <Row className="g-3">
          {switchSettings.map(item => renderSetting(item, true))}
        </Row>
      </div>
    </Tab.Pane>
  );
};

export default Main;
